import * as Sentry from '@sentry/nextjs';

import { CheckoutModes, type CheckoutMode } from '@/hooks/useCheckoutMode';

import { trackError } from '@/utils/errors';
import { getConnectedChain, trySwitchChain } from '@/utils/wallet';

import type { ExecutionContext, PreflightResult, TradeStrategy, TradeSubmitOutcome } from './types';

function sentryCheckoutMode(ctx: ExecutionContext): CheckoutMode {
  const { source } = ctx.quote;
  switch (source) {
    case 'advanced':
      return CheckoutModes.advanced;
    case 'cross-chain':
      return CheckoutModes.crossChain;
    case 'swap':
    case 'intents':
      return CheckoutModes.market;
    default: {
      const exhaustiveSource: never = source;
      return exhaustiveSource;
    }
  }
}

function sentryOrderType(ctx: ExecutionContext) {
  return ctx.advanced?.orderType ?? ctx.quote.source;
}

function setTradeSentryContext(ctx: ExecutionContext, blockNumber: string | undefined) {
  const raw = ctx.quote.raw;
  Sentry.setContext('transaction.context', {
    blockNumber: blockNumber ?? 'no_blocknumber',
    chainId: ctx.chainId,
    buyAmount: raw && 'buyAmount' in raw ? raw.buyAmount : 'no_data',
    sellAmount: raw && 'sellAmount' in raw ? raw.sellAmount : 'no_data',
    gas: raw && 'gas' in raw ? raw.gas : 'no_data',
    fees: raw && 'fees' in raw ? raw.fees : 'no_data',
    checkoutMode: sentryCheckoutMode(ctx),
    orderType: sentryOrderType(ctx),
  });
}

async function runStrategy<TPrepared extends PreflightResult>({
  strategy,
  ctx,
  prepared,
}: {
  strategy: TradeStrategy<TPrepared>;
  ctx: ExecutionContext;
  prepared: TPrepared;
}): Promise<TradeSubmitOutcome> {
  const { lane } = strategy;
  ctx.deps.dispatchCheckout({ type: 'submitStarted', lane });

  try {
    const result = await strategy.submit(ctx, prepared);

    if (result.success) {
      ctx.deps.dispatchCheckout({ type: 'submitSucceeded', lane });
      Sentry.setTag(`transaction.${result.outcome.kind}`, result.outcome.value);
      return { status: 'submitted', lane, id: result.outcome.value };
    }

    ctx.deps.dispatchCheckout({ type: result.rejected ? 'submitAborted' : 'submitFailed', lane });
    return result.rejected ? { status: 'rejected' } : { status: 'failed', error: result.error };
  } catch (error: unknown) {
    // A throw escaping submit is a lane bug; lanes own their expected failures.
    trackError(error);
    ctx.deps.dispatchCheckout({ type: 'submitFailed', lane });
    return {
      status: 'failed',
      error: error instanceof Error ? error : new Error(String(error)),
    };
  }
}

/** Orchestrates one submit attempt: preflight, chain switch, Sentry scope, and lifecycle dispatches around the lane's self-contained submit. */
export async function executeTrade<TPrepared extends PreflightResult>({
  strategy,
  ctx,
}: {
  strategy: TradeStrategy<TPrepared>;
  ctx: ExecutionContext;
}): Promise<TradeSubmitOutcome> {
  const prepared = strategy.preflight(ctx);
  if (!prepared) return { status: 'aborted' };

  if (prepared.chainFamily === 'evm' && getConnectedChain(ctx.ethereumWallet)?.id !== ctx.chainId) {
    const result = await trySwitchChain({
      chainId: ctx.chainId,
      switchChainAsync: ctx.switchChainAsync,
    });
    if (!result.success) return { status: 'aborted' };
  }

  const blockNumber = (await ctx.publicClient?.getBlockNumber())?.toString();

  const checkoutMode = sentryCheckoutMode(ctx);
  const orderType = sentryOrderType(ctx);

  return Sentry.withScope((scope) => {
    scope.setTag('user.account.address', ctx.accountAddress ?? 'no_address');
    scope.setTag('user.account.chain_id', ctx.chainId);
    scope.setTag('app.checkout_mode', checkoutMode);
    scope.setTag('transaction.order_type', orderType);
    setTradeSentryContext(ctx, blockNumber);

    return Sentry.startSpan(
      {
        name: 'executeTrade',
        op: 'submitTransaction',
        attributes: {
          'lane': strategy.lane,
          'user.account.address': ctx.accountAddress ?? 'no_address',
          'user.account.chain_id': ctx.chainId,
          'app.checkout_mode': checkoutMode,
          'transaction.order_type': orderType,
          ...(blockNumber ? { 'transaction.block_number': blockNumber } : {}),
          'hasPriceImpact': Boolean(ctx.quote.priceImpact),
        },
      },
      () => runStrategy({ strategy, ctx, prepared }),
    );
  });
}
